import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { supabase } from '../services/supabaseClient.ts';
import { handlePostLoginRedirect } from '../services/authRedirect.ts';
import { validatePassword, checkPasswordMatch, getPasswordStrength } from '../utils/validatePassword.ts';
import { Button } from '../components/ui/Button.tsx';
import { Input } from '../components/ui/Input.tsx';
import { Card } from '../components/ui/Card.tsx';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '../components/LanguageToggle.tsx';
import { Logo } from '../components/Logo.tsx';
import { AuthError } from '@supabase/supabase-js'; import { withRetry, withTimeout } from '../utils/retryUtils.ts';

export default function SignupScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isEmailValid = email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  const passwordError = validatePassword(password);
  const passwordMatchError = checkPasswordMatch(password, confirmPassword);
  const passwordStrength = getPasswordStrength(password);
  const isFormValid = isEmailValid && !passwordError && !passwordMatchError && password.length > 0;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleEmailBlur = () => {
    // No longer checking email existence on blur
    console.log('[SignupScreen] Email blur - skipping existence check');
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };
  
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  // Store signup source if coming from certain routes
  useEffect(() => {
    const source = localStorage.getItem('signupSource');
    if (!source && typeof window !== 'undefined' && window.document?.referrer.includes('/demo-results')) {
      localStorage.setItem('signupSource', 'demo');
      console.log('[SignupScreen] Set signupSource to demo from referrer');
    }
    
    // Also check current path for direct navigation
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      if (!source && currentPath === '/signup') {
        const urlParams = new URLSearchParams(window.location.search);
        const fromDemo = urlParams.get('from') === 'demo';
        if (fromDemo) {
          localStorage.setItem('signupSource', 'demo');
          console.log('[SignupScreen] Set signupSource to demo from URL param');
        }
      }
    }
    
    console.log('[SignupScreen] Current signupSource:', localStorage.getItem('signupSource'));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!isEmailValid) {
      setError('Please enter a valid email address.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    const complexityError = validatePassword(password);
    if (complexityError) {
      setError(complexityError);
      return;
    }
    
    setLoading(true);
    try {
      console.log('[SignupScreen] Attempting signup for:', email);
      
      const { data, error: supaError } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        }
      });
      
      if (supaError) {
        console.error('[SignupScreen] Signup failed:', supaError.message);
        
        // Handle specific error cases
        if (supaError.message.includes('User already registered') || 
            supaError.message.includes('already registered') ||
            supaError.message.includes('already been registered')) {
          setError("You're already registered! Please log in instead.");
          // Optionally auto-redirect to login after a delay
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
        } else {
          setError(supaError.message);
        }
        setLoading(false);
        return;
      }
      
      console.log('[SignupScreen] Signup success:', data);
      
      // Store email for potential resend if confirmation is required
      if (data.user && !data.session) {
        localStorage.setItem('pending_email_confirmation', email);
      }
      
      // Check if email confirmation is required
      if (data.user && !data.session) {
        setSuccess('Please check your inbox to confirm your email address before signing in.');
        console.log('[SignupScreen] Email confirmation required');
      } else if (data.session) {
        setSuccess('Signup successful! Redirecting...');
        console.log('[SignupScreen] Immediate login successful');
        // Clear any pending email confirmation since user is logged in
        localStorage.removeItem('pending_email_confirmation');
        await handlePostLoginRedirect();
      } else {
        setSuccess('Signup successful! Please check your email to confirm your account.');
        console.log('[SignupScreen] Signup completed, awaiting confirmation');
      }
      
    } catch (err: any) {
      console.error('[SignupScreen] Signup error:', err);
      setError(err.message || 'An unexpected error occurred during signup.');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (strength: 'weak' | 'medium' | 'strong') => {
    switch (strength) {
      case 'weak': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'strong': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
      <Navbar />
      <main className="container mx-auto px-6 py-8 flex-1">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-8">
          <h1 className="text-2xl font-bold mb-6 text-center flex items-center gap-2 justify-center"><Lock className="w-6 h-6" /> Sign Up</h1>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700">Email address</label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Email address"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                disabled={loading}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Password"
                value={password}
                onChange={handlePasswordChange}
                disabled={loading}
              />
              {password && (
                <div className="mt-1 text-xs">
                  <span className={`font-medium ${getStrengthColor(passwordStrength)}`}>
                    Password strength: {passwordStrength}
                  </span>
                </div>
              )}
              {passwordError && (
                <div className="mt-1 text-xs text-red-500">
                  {passwordError}
                </div>
              )}
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">Confirm Password</label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                disabled={loading}
              />
              {passwordMatchError && confirmPassword && (
                <div className="mt-1 text-xs text-red-500">
                  {passwordMatchError}
                </div>
              )}
            </div>
            {error && <div className="text-red-500 text-sm" role="alert">{error}</div>}
            {success && <div className="text-green-600 text-sm" role="status">{success}</div>}
            <button
              type="submit"
              className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading || !isFormValid}
            >
              {loading ? 'Signing up…' : 'Sign Up'}
            </button>
          </form>
          <div className="mt-6 flex flex-col gap-2 text-center text-sm">
            <Link to="/login" className="text-blue-600 hover:underline">Already have an account? Login</Link>
            <Link to="/upload" className="text-gray-500 hover:underline">Back to Upload</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
} 